import React, { useEffect, useState, useMemo } from 'react'
import {
    UpdateOrganizationRequest,
    useOrganization,
} from '../../../hooks/useOrganization'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    CircularProgress,
    IconButton,
    makeStyles,
    TextField,
    TableSortLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Switch,
    FormControlLabel,
    Box,
    Typography,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import { Organization } from '../../../models'
import { GLOBAL_TOP_BAR_HEIGHT } from '../../../constants'
import { toast } from 'react-toastify'

type Order = 'asc' | 'desc'

interface HeadCell {
    id: keyof Organization | 'actions'
    label: string
    sortable: boolean
}

const headCells: HeadCell[] = [
    { id: 'id', label: 'ID', sortable: true },
    { id: 'name', label: 'Name', sortable: true },
    { id: 'property_count', label: 'Property Count', sortable: true },
    { id: 'slug', label: 'Slug', sortable: true },
    {
        id: 'entrata_ar_transaction_code',
        label: 'Entrata AR Transaction Code',
        sortable: true,
    },
    { id: 'actions', label: 'Actions', sortable: false },
]

const useStyles = makeStyles((theme) => ({
    contentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        padding: theme.spacing(2),
    },
    searchContainer: {
        marginBottom: theme.spacing(2),
    },
    tableContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    tableWrapper: {
        flex: 1,
        overflow: 'auto',
        position: 'relative',
    },
    paginationContainer: {
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    loadingOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
    },
}))

interface EditOrganizationDialogProps {
    open: boolean
    organization: Organization | null
    onClose: () => void
    updateOrganization: (
        request: UpdateOrganizationRequest,
    ) => Promise<Organization>
    bulkUpdateBillingUnitRate: (
        organizationId: number,
        rate: number,
    ) => Promise<void>
}

const EditOrganizationDialog: React.FC<EditOrganizationDialogProps> = ({
    open,
    organization,
    onClose,
    updateOrganization,
    bulkUpdateBillingUnitRate,
}) => {
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        entrata_ar_transaction_code: '',
        archived: false,
    })
    const [bulkBillingRate, setBulkBillingRate] = useState('')
    const [isBulkUpdating, setIsBulkUpdating] = useState(false)

    useEffect(() => {
        if (organization) {
            setFormData({
                name: organization.name,
                slug: organization.slug || '',
                entrata_ar_transaction_code:
                    organization.entrata_ar_transaction_code?.toString() || '',
                archived: organization.archived || false,
            })
            setBulkBillingRate('')
        }
    }, [organization])

    const handleChange = (field: keyof typeof formData) => (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.value,
        }))
    }

    const handleArchivedChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setFormData((prev) => ({
            ...prev,
            archived: event.target.checked,
        }))
    }

    const handleSave = () => {
        const request: UpdateOrganizationRequest = {
            id: organization?.id || 0,
            body: {
                name: formData.name,
                slug: formData.slug,
                entrata_ar_transaction_code:
                    formData.entrata_ar_transaction_code,
                archived: formData.archived,
            },
        }
        updateOrganization(request).then(() => {
            toast.success('Organization updated successfully')
            onClose()
        })
    }

    const handleBulkUpdateBillingRate = async () => {
        if (!organization || !bulkBillingRate) {
            toast.error('Please enter a billing rate')
            return
        }

        const rate = parseFloat(bulkBillingRate)
        if (isNaN(rate) || rate < 0) {
            toast.error('Please enter a valid billing rate')
            return
        }

        setIsBulkUpdating(true)
        try {
            await bulkUpdateBillingUnitRate(organization.id, rate)
            toast.success(
                `Billing unit rate updated to ${rate} for all properties in ${organization.name}`,
            )
            setBulkBillingRate('')
        } catch (error) {
            toast.error('Failed to update billing unit rate')
        } finally {
            setIsBulkUpdating(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    label="Name"
                    value={formData.name}
                    onChange={handleChange('name')}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    fullWidth
                    label="Slug"
                    value={formData.slug}
                    onChange={handleChange('slug')}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    fullWidth
                    label="Entrata AR Transaction Code"
                    value={formData.entrata_ar_transaction_code}
                    onChange={handleChange('entrata_ar_transaction_code')}
                    margin="normal"
                    variant="outlined"
                    type="number"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.archived}
                            onChange={handleArchivedChange}
                            color="primary"
                        />
                    }
                    label="Archived"
                />

                {/* Bulk Billing Rate Update Section */}
                <Box
                    style={{
                        marginTop: 24,
                        padding: 16,
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        backgroundColor: '#fafafa',
                    }}
                >
                    <Typography variant="h6" style={{ marginBottom: 16 }}>
                        Bulk Update Billing Unit Rate
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginBottom: 16 }}
                    >
                        Update the billing unit rate for all properties in this
                        organization
                    </Typography>
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                        }}
                    >
                        <TextField
                            label="Billing Unit Rate"
                            type="text"
                            value={bulkBillingRate}
                            onChange={(e) => {
                                const value = e.target.value
                                // Remove any non-numeric characters
                                const numericValue = value.replace(
                                    /[^0-9]/g,
                                    '',
                                )

                                if (numericValue === '') {
                                    setBulkBillingRate('')
                                } else {
                                    // Convert to cents-based display
                                    const cents = parseInt(numericValue)
                                    const dollars = Math.floor(cents / 100)
                                    const remainingCents = cents % 100
                                    const formattedCents = remainingCents
                                        .toString()
                                        .padStart(2, '0')
                                    setBulkBillingRate(
                                        `${dollars}.${formattedCents}`,
                                    )
                                }
                            }}
                            onBlur={(e) => {
                                const value = e.target.value
                                if (value !== '') {
                                    const numValue = parseFloat(value)
                                    if (!isNaN(numValue)) {
                                        setBulkBillingRate(numValue.toFixed(2))
                                    }
                                }
                            }}
                            variant="outlined"
                            size="small"
                            style={{ flex: 1 }}
                            placeholder="0.00"
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleBulkUpdateBillingRate}
                            disabled={!bulkBillingRate || isBulkUpdating}
                            style={{ minWidth: 120 }}
                        >
                            {isBulkUpdating ? 'Updating...' : 'Update All'}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export const OrganizationList = () => {
    const classes = useStyles()
    const {
        organizationList,
        getOrganizationList,
        updateOrganization,
        bulkUpdateBillingUnitRate,
    } = useOrganization()
    const [isLoading, setIsLoading] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [showArchived, setShowArchived] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)
    const [orderBy, setOrderBy] = useState<keyof Organization>('id')
    const [order, setOrder] = useState<Order>('asc')
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [
        selectedOrganization,
        setSelectedOrganization,
    ] = useState<Organization | null>(null)

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true)
            await getOrganizationList({
                detail: true,
                apartment_only: true,
                archived: showArchived,
            })
            setIsLoading(false)
        }
        fetchData()
    }, [showArchived])

    const handleRequestSort = (property: keyof Organization) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const filteredOrganizations = useMemo(() => {
        return organizationList.filter((org) =>
            Object.values(org).some((value) =>
                String(value).toLowerCase().includes(searchQuery.toLowerCase()),
            ),
        )
    }, [organizationList, searchQuery])

    const sortedAndFilteredOrganizations = useMemo(() => {
        const comparator = (a: Organization, b: Organization) => {
            if (orderBy) {
                const aValue = a[orderBy]
                const bValue = b[orderBy]

                // Special handling for numeric fields
                if (orderBy === 'id' || orderBy === 'property_count') {
                    if (order === 'desc') {
                        return (bValue as number) - (aValue as number)
                    }
                    return (aValue as number) - (bValue as number)
                }

                // Handle string comparison for other fields
                if (order === 'desc') {
                    return (bValue ?? '')
                        .toString()
                        .localeCompare((aValue ?? '').toString())
                }
                return (aValue ?? '')
                    .toString()
                    .localeCompare((bValue ?? '').toString())
            }
            return 0
        }

        return [...filteredOrganizations].sort(comparator)
    }, [filteredOrganizations, order, orderBy])

    const paginatedOrganizations = useMemo(() => {
        const startIndex = page * rowsPerPage
        return sortedAndFilteredOrganizations.slice(
            startIndex,
            startIndex + rowsPerPage,
        )
    }, [sortedAndFilteredOrganizations, page, rowsPerPage])

    const handleEdit = (organization: Organization) => {
        setSelectedOrganization(organization)
        setEditDialogOpen(true)
    }

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false)
        setSelectedOrganization(null)
    }

    return (
        <div className={classes.contentWrapper}>
            <div
                className={classes.searchContainer}
                style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
            >
                <TextField
                    style={{ maxWidth: 500, minWidth: 500 }}
                    label="Search Organizations"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Show Archived"
                />
            </div>

            <div className={classes.tableContainer}>
                <TableContainer
                    component={Paper}
                    className={classes.tableWrapper}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {headCells.map((headCell) => (
                                    <TableCell
                                        key={headCell.id}
                                        sortDirection={
                                            orderBy === headCell.id
                                                ? order
                                                : false
                                        }
                                    >
                                        {headCell.sortable ? (
                                            <TableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={
                                                    orderBy === headCell.id
                                                        ? order
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleRequestSort(
                                                        headCell.id as keyof Organization,
                                                    )
                                                }
                                            >
                                                {headCell.label}
                                            </TableSortLabel>
                                        ) : (
                                            headCell.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedOrganizations.map((org) => (
                                <TableRow key={org.id} hover>
                                    <TableCell>{org.id}</TableCell>
                                    <TableCell>{org.name}</TableCell>
                                    <TableCell>{org.property_count}</TableCell>
                                    <TableCell>{org.slug}</TableCell>
                                    <TableCell>
                                        {org.entrata_ar_transaction_code}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEdit(org)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <div className={classes.paginationContainer}>
                    <TablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        component="div"
                        count={filteredOrganizations.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </div>
            </div>

            {isLoading && (
                <div className={classes.loadingOverlay}>
                    <CircularProgress />
                </div>
            )}

            <EditOrganizationDialog
                open={editDialogOpen}
                organization={selectedOrganization}
                onClose={handleCloseEditDialog}
                updateOrganization={updateOrganization}
                bulkUpdateBillingUnitRate={bulkUpdateBillingUnitRate}
            />
        </div>
    )
}
